import { useEffect, useState } from 'react';
import { DrawerNoHeader } from '../../../components/drawer/DrawerNoHeader';
import { TabsPaymentStudent } from './TabsPaymentStudent';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPaymentByUserId } from '../../../redux/actions/payment';
import { AvatarWithInitials } from '../../../components/avatar/AvatarWithInitials';
import { Button } from '../../../components/buttons/Button';
import { DrawerProfileUser } from '../../profile/drawerProfileUser/DrawerProfileUser';

export const DrawerPaymentStudent = ({
    isOpenDrawerPayment,
    toggleDrawerPayment,
    userId
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const { paymentsByUser } = useSelector((state) => state.payment);
    const [footerActions, setFooterActions] = useState(null);
    const [isOpenDrawerProfile, setIsOpenDrawerProfile] = useState(false);

    const {
        firstName = '',
        secondName = '',
        lastName = '',
        secondSurname = '',
        matricula = ''
    } = user || {};

    useEffect(() => {
        if (isOpenDrawerPayment && userId) {
            dispatch(getAllPaymentByUserId(userId));
        }
    }, [isOpenDrawerPayment, userId, dispatch]);

    // Encontrar el pago rechazado (solo puede haber uno)
    const rejectedPayment = paymentsByUser.find(payment => payment.status === 'rechazado') || null;

    // Verificar si el usuario puede subir un nuevo pago
    // Puede subir si: NO tiene pagos, O todos sus pagos están validados
    // NO puede subir si: tiene algún pago pendiente o rechazado
    const canUploadPayment = paymentsByUser.length === 0 ||
        paymentsByUser.every(payment => payment.status === 'validado');

    // Verificar si hay un pago rechazado que debe ser editado
    const hasRejectedPayment = rejectedPayment !== null;

    // Verificar si se está cargando un pago
    const isUploading = footerActions?.loading || false;

    // Función para manejar el cierre del drawer (prevenir cierre si está subiendo)
    const handleToggleDrawer = () => {
        if (!isUploading) {
            toggleDrawerPayment();
        }
    };

    const toggleDrawerProfile = () => {
        setIsOpenDrawerProfile((prevState) => !prevState);
    };

    const handleOpenProfileDrawer = () => {
        setIsOpenDrawerProfile(true);
        // Cerrar el drawer de pagos cuando se abre el de perfil
        toggleDrawerPayment();
    };

    return (
        <>
            <DrawerNoHeader
                isOpen={isOpenDrawerPayment}
                toggleDrawer={handleToggleDrawer}
                direction='right'
                footer={
                    <div className='flex items-center justify-end gap-4 overflow-hidden'>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={isUploading}
                            onClick={handleToggleDrawer}
                        >
                            Cerrar
                        </Button>
                        {footerActions && (
                            <Button
                                type="button"
                                variant="primary"
                                loading={footerActions.loading}
                                disabled={footerActions.disabled}
                                onClick={footerActions.onSubmit}
                            >
                                {footerActions.buttonLabel || 'Subir Comprobante'}
                            </Button>
                        )}
                    </div>
                }
            >
                <div className='w-full flex flex-col h-full overflow-hidden'>
                    {/* Card única con toda la información - Header Fijo */}
                    <div className='w-full bg-white rounded-lg border border-gray-200 flex-shrink-0'>
                        {/* Header con gradiente */}
                        <div className='bg-gradient-to-r from-indigo-500 to-purple-600 p-4 rounded-t-lg'>
                            <div className='flex items-center space-x-3 md:space-x-4'>
                                {/* Avatar con iniciales */}
                                <div className='relative flex-shrink-0'>
                                    <AvatarWithInitials
                                        firstName={firstName}
                                        lastName={lastName}
                                        size="md"
                                    />
                                </div>

                                {/* Información principal */}
                                <div className='flex-1 min-w-0'>
                                    <div className='flex items-center gap-2 flex-wrap'>
                                        <h3 className='text-white font-bold text-lg md:text-xl lg:text-2xl capitalize tracking-tight truncate'>
                                            {`${firstName} ${secondName ? secondName : ''} ${lastName} ${secondSurname ? secondSurname : ''}`.trim() || 'Usuario'}
                                        </h3>
                                    </div>

                                    {/* Info badges - solo en desktop */}
                                    <div className='hidden md:flex flex-wrap gap-2 mt-2'>
                                        {matricula && (
                                            <div className='inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/30'>
                                                <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                                </svg>
                                                <span className='text-white text-sm font-semibold'>
                                                    {matricula}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs - Ocupa el espacio restante con scroll */}
                    <div className='flex-1 overflow-hidden'>
                        <TabsPaymentStudent
                            userId={userId}
                            isOpen={isOpenDrawerPayment}
                            canUploadPayment={canUploadPayment}
                            hasRejectedPayment={hasRejectedPayment}
                            rejectedPayment={rejectedPayment}
                            onRegisterFooterActions={setFooterActions}
                            onOpenProfileDrawer={handleOpenProfileDrawer}
                        />
                    </div>
                </div>
            </DrawerNoHeader>

            {/* Drawer de perfil - Fuera del DrawerNoHeader */}
            <DrawerProfileUser
                isOpenDrawerProfile={isOpenDrawerProfile}
                toggleDrawerProfile={toggleDrawerProfile}
                userId={userId}
                refreshTable={false}
            />
        </>
    );
};
